import { memo } from 'react';
import cs from 'classnames';
import useNavMenuData from '../../hooks/useNavMenuData';
import usePermissionsCheck from '../../hooks/usePermissionsCheck';
import { useUserAuth } from '~/components/Auth';

import MenuContent from './component/MenuContent';
import NavMenuHead from './component/MenuHead';
import { StyledNavMenu, StyledNavMenuAnchorTag } from './styles';
import { NavMenuProps } from './types';

const NavMenu = ({
  intl,
  menuId,
  linkPattern,
  iconType,
  classNames,
  isCollapsed,
  subMenu = [],
  menuName,
  permissionsMap,
}: NavMenuProps) => {
  const { isMenuOpen, iconTypeKey, onMenuExpandHandler, activeRouteId } = useNavMenuData({
    subMenu,
    linkPattern,
  });

  const { sessionId, company: { id: companyId = '' } = {} } = useUserAuth();

  const { isLoading, isSubMenuVisible } = usePermissionsCheck({
    permissionsMap,
    subMenu,
    sessionId,
    companyId,
  });

  const navMenuClassName = cs(`${classNames?.menuName}`, {
    'open-menu': isMenuOpen,
    'close-menu': !isMenuOpen,
    'active-menu': !!activeRouteId,
    'collapsed-navigation-menu': isCollapsed,
  });

  if (!subMenu.length) {
    return (
      <StyledNavMenuAnchorTag
        data-testid="navigation-menu-link"
        to={linkPattern}
        className={`menu-wrapper-${menuId}`}
      >
        <NavMenuHead
          iconType={iconType}
          iconTypeKey={iconTypeKey}
          menuName={menuName}
          firstSubMenu=""
          isRouteActive={!!activeRouteId}
          isCollapsed={isCollapsed}
          handleClick={onMenuExpandHandler}
          isOnlyMenuItem={true}
        />
      </StyledNavMenuAnchorTag>
    );
  }

  return (
    <StyledNavMenu
      data-testid="navigation-menu"
      className={`menu-wrapper-${menuId} ${navMenuClassName}`}
    >
      <MenuContent
        menuId={menuId}
        intl={intl}
        subMenu={subMenu}
        iconType={iconType}
        isMenuOpen={isMenuOpen}
        isCollapsed={!!isCollapsed}
        iconTypeKey={iconTypeKey}
        activeRouteId={activeRouteId as string}
        menuName={menuName}
        onMenuExpandHandler={onMenuExpandHandler}
        routeItemName={classNames?.routeItemName}
        isLoading={isLoading}
        isSubMenuVisible={isSubMenuVisible}
      />
    </StyledNavMenu>
  );
};

export default memo(NavMenu);
